import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import {
  FDBodyText,
  FDBox,
  FDStack,
  FDTable,
  FDTableBody,
  FDTableCell,
  FDTableHead,
  FDTableRow,
} from '@facet-wealth/design';
import HoldingsBarChart
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/HoldingsBarChart/HoldingBarChart';
import useMobileLayout from 'Hooks/useMobileLayout';
import useManagedAccountPageEventLogger
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/managedAccountPage.eventLogging';
import {
  useManagedAccountPortfolio,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/useManagedAccountPortfolio';
import {
  ManagedAccountTableRowGroup,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/ManagedAccountTableRowGroup';
import { USDCurrencyFullFormatter } from 'Helpers/Formats';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import HoldingCard from 'Components/UI/HoldingCard/HoldingCard';
import {
  HoldingCardGroup,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/HoldingCardGroup';
import { dataAttrs } from 'Helpers/Testing';

export default function ManagedAccountPortfolioWithGroups({ portfolio }) {
  const isMobile = useMobileLayout();
  const { logPortfolioChart, logHoldingsTable } = useManagedAccountPageEventLogger();

  const {
    selectedCategory,
    setSelectedCategory,
    filteredCategories,
    categories,
    filteredHoldingsGroups,
    usingMockData,
    categoryIDToCategoryMap,
    filteredHoldingsGroupsWithoutUngrouped,
    filteredUngroupedHoldings,
  } = useManagedAccountPortfolio(portfolio);

  if (!filteredHoldingsGroups?.length) return <FDBox><FDBodyText value={'No Account Holdings'} /></FDBox>;

  return (
    <>
      <FDStack direction={isMobile ? 'column' : 'row'} spacing={2}>
        <FDStack direction={isMobile ? 'column' : 'row'} width={isMobile ? '100%' : '30%'} {...dataAttrs('managed-account-allocation-distribution')}>
          <HoldingsBarChart
            data={categories}
            disable={usingMockData}
            onSectionClick={(label, id) => {
              logPortfolioChart(`Set Category ${id === selectedCategory ? 'All' : label}`);
              setSelectedCategory(id === selectedCategory ? null : id);
            }}
            selectedSection={selectedCategory}
          />
        </FDStack>
        <FDBox width={isMobile ? '100%' : '70%'} {...dataAttrs('managed-account-allocation-holdings')}>
          {isMobile ? (
            <>
              {filteredHoldingsGroupsWithoutUngrouped.map((holdingsGroup) => (
                <HoldingCardGroup
                  key={holdingsGroup.name}
                  holdingsGroup={holdingsGroup}
                  categoryIDToCategoryMap={categoryIDToCategoryMap}
                  filteredCategories={filteredCategories}
                />
              ))}
              {filteredUngroupedHoldings.map((holding) => {
                const category = categoryIDToCategoryMap.get(holding.attr('tickerClassification'));
                return (
                  <HoldingCard
                    key={holding.id()}
                    holdingResource={holding}
                    holdingClassificationColor={category.color}
                    expandedByDefault={filteredCategories.length === 1}
                  />
                );
              })}
            </>
          ) : null}
          {!isMobile
            ? (
              <FDTable>
                <FDTableHead>
                  <FDTableRow>
                    <FDTableCell>{'Symbol'}</FDTableCell>
                    <FDTableCell align={'right'}>{'Quantity'}</FDTableCell>
                    <FDTableCell align={'right'}>{'Price'}</FDTableCell>
                    <FDTableCell align={'right'}>{'Value'}</FDTableCell>
                    <FDTableCell align={'right'}>{'Cost Basis'}</FDTableCell>
                    <FDTableCell align={'right'}>{'Gain/Loss'}</FDTableCell>
                  </FDTableRow>
                </FDTableHead>
                <FDTableBody>
                  {filteredHoldingsGroupsWithoutUngrouped.map((holdingsGroup) => (
                    <ManagedAccountTableRowGroup key={holdingsGroup.name} holdingsGroup={holdingsGroup} categoryIDToCategoryMap={categoryIDToCategoryMap} selectedCategory={selectedCategory} />
                  ))}
                  {filteredUngroupedHoldings.map((holding) => {
                    const category = categoryIDToCategoryMap.get(holding.attr('tickerClassification'));
                    return (
                      <FDTableRow
                        key={holding.id()}
                        onClick={() => logHoldingsTable(`Holding for category ${category.label}`)}
                        hover
                      >
                        <FDTableCell>
                          <FDStack direction={'row'} alignItems={'center'}>
                            {category ? <FiberManualRecordIcon
                              style={{ color: category.color }}
                              onClick={() => logHoldingsTable(`Color icon for category ${category.label}`)}
                            /> : null}
                            {holding.attr('ticker')}
                          </FDStack>
                        </FDTableCell>
                        <FDTableCell align={'right'}>{holding.attr('quantity')}</FDTableCell>
                        <FDTableCell align={'right'}>
                          {USDCurrencyFullFormatter.format(holding.attr('currentPrice'))}
                        </FDTableCell>
                        <FDTableCell align={'right'}>
                          {USDCurrencyFullFormatter.format(holding.attr('currentValue'))}
                        </FDTableCell>
                        <FDTableCell align={'right'}>
                          {USDCurrencyFullFormatter.format(holding.attr('costBasis'))}
                        </FDTableCell>
                        <FDTableCell align={'right'}>
                          {USDCurrencyFullFormatter.format(holding.attr('dollarGain'))}
                        </FDTableCell>
                      </FDTableRow>
                    );
                  })}
                </FDTableBody>
              </FDTable>
            ) : null}
        </FDBox>
      </FDStack>
    </>
  );
}
ManagedAccountPortfolioWithGroups.defaultProps = {
  portfolio: undefined,
};
ManagedAccountPortfolioWithGroups.propTypes = {
  portfolio: PropTypes.instanceOf(fr.Resource),
};
